import { AlertTriangle, Bot, Clock, Database } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import type { McpStorageWriteApprovalRequest } from '../../../types/mcpUiControl';
import { SEVERITY_LABEL, storageApprovalPresentation, type StorageApprovalSeverity } from './storageApprovalPresentation';
import { useReturnFocus } from './useReturnFocus';

type Props = {
  request: McpStorageWriteApprovalRequest | null;
  onApprove: (requestId: string) => void;
  onDeny: (requestId: string) => void;
};

const BADGE_VARIANT: Record<StorageApprovalSeverity, 'destructive' | 'default' | 'secondary'> = {
  destructive: 'destructive',
  modifies: 'default',
  creates: 'secondary',
};

/**
 * Round B U29 / A14 / A20: the heading asks the question ("Delete 3 storage items?") with "Requested by AI · tool" as
 * secondary detail; the severity badge follows the operation; every target is listed once, after a short consequence
 * summary (a line saying access widens comes first, in the warning colour). Deny is the AlertDialog's Cancel, so it
 * takes the initial focus; Radix returns focus to where it was when the dialog goes.
 */
export function McpStorageWriteApprovalDialog({ request, onApprove, onDeny }: Props) {
  const [seconds, setSeconds] = useState<number | null>(null);
  useReturnFocus(request?.requestId ?? null);

  useEffect(() => {
    if (!request) {
      setSeconds(null);
      return undefined;
    }
    const end = Date.now() + (request.timeoutMs ?? 60_000);
    const update = () => setSeconds(Math.max(0, Math.ceil((end - Date.now()) / 1000)));
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [request]);

  if (!request) return null;
  const deny = () => onDeny(request.requestId);
  const view = storageApprovalPresentation(request);

  return (
    <AlertDialog open onOpenChange={(open) => !open && deny()}>
      {/* Bounded to the window: a long summary + target list scrolls in the body while the header and the
          Approve / Deny buttons stay on screen (a 25-operation batch pushed them past the viewport). */}
      <AlertDialogContent className="max-w-lg max-h-[90vh] grid-rows-[auto_minmax(0,1fr)_auto]" data-testid="mcp-storage-write-approval-dialog" data-request-id={request.requestId}>
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-warning/10">
              <Bot className="h-5 w-5 text-warning" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <AlertDialogTitle className="text-lg" data-testid="mcp-storage-approval-heading">{view.heading}</AlertDialogTitle>
              <AlertDialogDescription className="mt-1">
                Requested by AI{view.tool && <> · <code className="font-mono text-xs">{view.tool}</code></>}
              </AlertDialogDescription>
            </div>
          </div>
        </AlertDialogHeader>
        <div className="min-h-0 space-y-4 overflow-y-auto py-2" data-testid="mcp-storage-approval-body">
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <Database className="h-3.5 w-3.5" aria-hidden />
            <span data-testid="mcp-storage-approval-connection">{request.connectionName || request.connectionId}</span>
            <Badge variant={BADGE_VARIANT[view.severity]} data-testid="mcp-storage-approval-severity" data-severity={view.severity}>{SEVERITY_LABEL[view.severity]}</Badge>
          </div>
          {view.risks.length > 0 && (
            <ul className="space-y-1 rounded-md border border-warning/40 bg-warning/10 p-3 text-sm text-foreground" data-testid="mcp-storage-approval-risks">
              {view.risks.map((line) => (
                <li key={line} className="flex items-start gap-2"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />{line}</li>
              ))}
            </ul>
          )}
          {request.targets && (
            // Plan §3.5 (D15): the complete target list — on screen, scrollable by wheel or keyboard, never behind a click.
            // C2 N15: right under the risk line, before the (possibly long) summary, so what is affected is above the fold.
            <section className="space-y-1.5" aria-label={request.targets.heading}>
              <div className="text-xs font-medium text-foreground">{request.targets.heading}</div>
              <ul
                role="list"
                tabIndex={0}
                data-testid="mcp-storage-approval-targets"
                className="max-h-64 overflow-y-auto rounded-md border border-border bg-muted/30 px-3 py-2 font-mono text-[11px] leading-relaxed text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {request.targets.lines.map((line, index) => <li key={index} className="break-words">{line}</li>)}
              </ul>
            </section>
          )}
          {view.summary.length > 0 && (
            <ul className="list-disc space-y-1 pl-5 text-sm text-foreground" data-testid="mcp-storage-approval-summary">
              {view.summary.map((line) => <li key={line} className="break-words">{line}</li>)}
            </ul>
          )}
          {seconds !== null && seconds <= 30 && (
            <div className="flex items-center gap-2 text-xs text-warning">
              <Clock className="h-3.5 w-3.5" aria-hidden />
              <span>{seconds > 0 ? `Request expires in ${seconds}s (will be denied)` : 'Request expired'}</span>
            </div>
          )}
        </div>
        <AlertDialogFooter className="gap-2 sm:gap-0">
          {/* Cancel closes through onOpenChange → deny (never twice), and is Radix's initial focus. */}
          <AlertDialogCancel data-testid="mcp-storage-approval-deny">Deny</AlertDialogCancel>
          <Button onClick={() => onApprove(request.requestId)} disabled={seconds === 0} variant={view.severity === 'destructive' ? 'destructive' : 'default'}>Approve & Execute</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
